/* The route tree, shared by two consumers: createBrowserRouter in App.tsx (the
 * running site) and createStaticHandler in src/prerender/entry.tsx (the
 * build-time prerender). It lives here so those two cannot drift — a route in
 * one and not the other ships a page whose static HTML and client render
 * disagree. */
import type { RouteObject } from 'react-router';
import { redirect } from 'react-router';
import Root from './root';
import LocaleLayout from './locale';
import HomePage from './index';
import PrivacyRoute from './privacy';
import TermsRoute from './terms';
import SubprocessorsRoute from './subprocessors';
import AboutRoute from './about';
import TrustRoute from './trust';
import NotFound from './NotFound';
import { DEFAULT_LOCALE } from '../strings/types';

export const routeTree: RouteObject[] = [
  {
    path: '/',
    loader: () => redirect(`/${DEFAULT_LOCALE}/`),
  },
  {
    path: '/:locale',
    element: <Root />,
    children: [
      {
        path: '',
        element: <LocaleLayout />,
        children: [
          { index: true, element: <HomePage /> },
          /* caseSensitive keeps the router in agreement with the
           * case-sensitive parsePath (useDocumentHead): /en/Privacy is a 404
           * in both the view and the head, instead of the Privacy page
           * rendering under a noindexed not-found head. */
          { path: 'privacy', element: <PrivacyRoute />, caseSensitive: true },
          { path: 'terms', element: <TermsRoute />, caseSensitive: true },
          { path: 'subprocessors', element: <SubprocessorsRoute />, caseSensitive: true },
          { path: 'about', element: <AboutRoute />, caseSensitive: true },
          { path: 'trust', element: <TrustRoute />, caseSensitive: true },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
  {
    /* Nearly everything is outranked by /:locale, but an EMPTY first segment
     * (double-leading-slash URLs like //x — routine link-concatenation
     * artifacts) matches neither / nor /:locale and would otherwise hit the
     * router's raw error boundary with no noindex. Root with no :locale param
     * coerces to DEFAULT_LOCALE and renders NotFound (Decision 3) — the old
     * redirect() here was the coerced-homepage soft-404 this phase removes. */
    path: '*',
    element: <Root />,
  },
];
